/**
 * The `ff_ui` cookie that records which app a staff browser gets on THIS hostname. When it reads
 * `vue3`, the root `middleware.js` (Vercel Routing Middleware) proxies every request to the Vue 3
 * deployment (docs/superpowers/specs/2026-08-20-vue3-crossover-cookie-rewrite-design.md). When it
 * reads `legacy`, the browser has opted out: this app is served and the default-on push leaves
 * it alone (see LEGACY_COOKIE_VALUE). Pure over an injectable document/location so it unit-tests
 * without a browser.
 *
 * Counterpart: formfactories-vue3/src/lib/uiVersionCookie.ts — keep the strings identical. That
 * app writes `ff_ui=legacy` to come back here, and clears the cookie only on sign-out.
 */

export const UI_VERSION_COOKIE = 'ff_ui'
export const VUE3_COOKIE_VALUE = 'vue3'
/**
 * The persistent opt-OUT. This host serves the legacy app to a browser holding it (it is not
 * `vue3`), and the default-on push in `vue3DefaultOn.js` leaves that browser alone.
 * Design: formfactories-vue3 docs/superpowers/specs/2026-10-12-vue3-default-on-staff-design.md
 */
export const LEGACY_COOKIE_VALUE = 'legacy'

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365

/**
 * The exact `Set-Cookie` value that expires the cookie. Shared, not duplicated, so a drifted
 * Path or SameSite cannot leave a second, un-clearable cookie behind.
 */
export const CLEAR_COOKIE_HEADER_VALUE = `${UI_VERSION_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`

/** `ff_ui=<value>` with the one-year, site-wide attributes; `Secure` only when asked. */
function cookieString (value, secure) {
  const attrs = ['Path=/', `Max-Age=${ONE_YEAR_SECONDS}`, 'SameSite=Lax']
  if (secure) attrs.push('Secure')
  return `${UI_VERSION_COOKIE}=${value}; ${attrs.join('; ')}`
}

/**
 * The exact `Set-Cookie` value for the opt-out. The root `middleware.js` sends it verbatim for
 * the `?ff_ui=legacy` escape hatch; it only runs on https deployments, hence always `Secure`.
 */
export const LEGACY_COOKIE_HEADER_VALUE = cookieString(LEGACY_COOKIE_VALUE, true)

function holds (jar, value) {
  return jar.cookie
    .split(';')
    .map(part => part.trim())
    .includes(`${UI_VERSION_COOKIE}=${value}`)
}

/** True when the browser currently holds `ff_ui=vue3`. */
export function hasVue3Cookie (jar = document) {
  return holds(jar, VUE3_COOKIE_VALUE)
}

/** True when the browser currently holds the `ff_ui=legacy` opt-out. */
export function hasLegacyCookie (jar = document) {
  return holds(jar, LEGACY_COOKIE_VALUE)
}

/**
 * Opt this browser into Vue 3 for a year. `Secure` is dropped on plain http (a local dev server)
 * because browsers refuse a Secure cookie from an insecure page; every deployed host is https.
 */
export function setVue3Cookie (jar = document, loc = window.location) {
  jar.cookie = cookieString(VUE3_COOKIE_VALUE, loc.protocol === 'https:')
}

/** Expire the cookie: no choice recorded, so this host serves the legacy app. */
export function clearVue3Cookie (jar = document) {
  jar.cookie = CLEAR_COOKIE_HEADER_VALUE
}
